import { useBufferedFilteredEntries } from 'features/drilldown/rolodex/dataHelpers/useFilteredEntries'
import {
  EnrichedOntologyFieldDefinition,
  extractValueFromEntry,
  OntologyFieldType,
  OntologyRolodexEntryType,
} from 'features/dynamicOntology'
import { useMemo } from 'react'
import useSWR from 'swr'
import { GeckoEntry, RolodexHistogramFilter } from 'types'

import {
  DrilldownMetricConfig,
  FieldOperation,
  MetricCalculationType,
} from './MetricsPanelConfig'

// Result type for metric calculations
interface MetricResult {
  value: number
  isLoading: boolean
  error?: string
}

// Helper type for calculation functions
type CalculationFunction = (
  data: ReadonlyArray<GeckoEntry>,
  config: DrilldownMetricConfig,
  filters?: ReadonlyArray<RolodexHistogramFilter>
) => number

const calculateCount: CalculationFunction = (
  data: ReadonlyArray<GeckoEntry>
) => {
  return data.length
}

const calculateSum: CalculationFunction = (
  data: ReadonlyArray<GeckoEntry>,
  config: DrilldownMetricConfig
) => {
  return data.reduce((sum, entry) => {
    return sum + getCalculatedValue(entry, config)
  }, 0)
}

const calculateMin: CalculationFunction = (
  data: ReadonlyArray<GeckoEntry>,
  config: DrilldownMetricConfig
) => {
  if (data.length === 0) return 0

  const values = data.map((entry) => getCalculatedValue(entry, config))
  return Math.min(...values)
}

const calculateMax: CalculationFunction = (
  data: ReadonlyArray<GeckoEntry>,
  config: DrilldownMetricConfig
) => {
  if (data.length === 0) return 0

  const values = data.map((entry) => getCalculatedValue(entry, config))
  return Math.max(...values)
}

const calculateMean: CalculationFunction = (
  data: ReadonlyArray<GeckoEntry>,
  config: DrilldownMetricConfig
) => {
  if (data.length === 0) return 0

  const sum = data.reduce((acc, entry) => {
    return acc + getCalculatedValue(entry, config)
  }, 0)
  return sum / data.length
}

// Calculation function lookup table
const calculators: Record<MetricCalculationType, CalculationFunction> = {
  count: calculateCount,
  sum: calculateSum,
  min: calculateMin,
  max: calculateMax,
  mean: calculateMean,
}

/**
 * Main metric calculation service
 * Routes to specific calculation functions based on the metric configuration
 */
export async function calculateMetric(
  config: DrilldownMetricConfig,
  data: ReadonlyArray<GeckoEntry>,
  filters?: ReadonlyArray<RolodexHistogramFilter>
): Promise<MetricResult> {
  try {
    const calculator: CalculationFunction | undefined =
      calculators[config.calculationType]
    if (!calculator) {
      throw new Error(`Unsupported calculation type: ${config.calculationType}`)
    }

    let value = calculator(data, config, filters)

    // Apply precision if specified
    if (config.numDecimals !== undefined) {
      value = Number(value.toFixed(config.numDecimals))
    }

    return {
      value,
      isLoading: false,
    }
  } catch (error) {
    return {
      value: 0,
      isLoading: false,
      error: error instanceof Error ? error.message : 'Calculation error',
    }
  }
}

// - - - - - - - - - - - CALCULATION FUNCTIONS - - - - - - - - - - -
function calculateField(
  entry: GeckoEntry,
  rolodexType: OntologyRolodexEntryType,
  field: string
): number {
  const fieldDef: EnrichedOntologyFieldDefinition = {
    name: field,
    type: 'number' as OntologyFieldType,
    entryType: rolodexType,
    key: field,
    keyOrField: 'field' as const,
  }
  const value = extractValueFromEntry(entry, fieldDef)
  return typeof value === 'number' ? value : 0
}

function calculateFieldOperation(
  entry: GeckoEntry,
  config: DrilldownMetricConfig
): number {
  const operationResults: number[] = []

  // fieldOperators was checked above, so we can safely access it
  for (let i = 0; i < config.fieldOperators!.length; i++) {
    const operator = config.fieldOperators![i]
    const { operation, field1, field2 } = operator

    let num1: number
    let num2: number

    // if the given field is a result_<index>, use the result at that index
    if (field1.startsWith('result_')) {
      const resultIndex = parseInt(field1.split('_')[1], 10)
      num1 = operationResults[resultIndex] || 0
    } else {
      const fieldDef1: EnrichedOntologyFieldDefinition = {
        name: field1,
        type: 'number' as OntologyFieldType,
        entryType: config.rolodexType,
        key: field1,
        keyOrField: 'field' as const,
      }
      const value1 = extractValueFromEntry(entry, fieldDef1)
      num1 = typeof value1 === 'number' ? value1 : 0
    }

    let operationResult: number

    // if no operation or field2, just return field1 value
    if (!operation || !field2) {
      operationResult = num1
    } else {
      // Handle field2 - could be a field reference or result reference
      if (field2.startsWith('result_')) {
        const resultIndex = parseInt(field2.split('_')[1], 10)
        num2 = operationResults[resultIndex] || 0
      } else {
        const fieldDef2: EnrichedOntologyFieldDefinition = {
          name: field2,
          type: 'number' as OntologyFieldType,
          entryType: config.rolodexType,
          key: field2,
          keyOrField: 'field' as const,
        }
        const value2 = extractValueFromEntry(entry, fieldDef2)
        num2 = typeof value2 === 'number' ? value2 : 0
      }

      operationResult = applyFieldOperation(num1, num2, operation)
    }

    operationResults.push(operationResult)
  }

  // Return the final result (last operation result)
  return operationResults[operationResults.length - 1] || 0
}

// Helper function to get the value for an entry (with optional field operations)
function getCalculatedValue(
  entry: GeckoEntry,
  config: DrilldownMetricConfig
): number {
  if (!config.field && !config.fieldOperators) {
    throw new Error('Either field or fieldOperators are required')
  }

  // Prioritizes field extraction over field operations
  if (config.field) {
    return calculateField(entry, config.rolodexType, config.field)
  }

  // if field does not exist, calculate field operation
  return calculateFieldOperation(entry, config)
}

function applyFieldOperation(
  a: number,
  b: number,
  operation: FieldOperation
): number {
  switch (operation) {
    case 'add':
      return a + b
    case 'subtract':
      return a - b
    case 'multiply':
      return a * b
    case 'divide':
      if (b === 0) {
        throw new Error('Division by zero is not allowed')
      }
      return a / b
    default:
      return 0
  }
}

// - - - - - - - - - - - HELPER FUNCTIONS - - - - - - - - - - -
/**
 * React hook to calculate a metric value
 * Integrates with the drilldown data system and provides loading states
 */
export function useMetricCalculation(
  config: DrilldownMetricConfig
): MetricResult {
  const { data: entries, isLoading: isLoadingData } =
    useBufferedFilteredEntries({
      entryType: config.rolodexType,
      dataKey: 'entries',
      limit: undefined,
    })

  const filteredEntries = useMemo(
    () => entries.filter((entry) => entry.type === config.rolodexType),
    [entries, config.rolodexType]
  )

  const {
    data: result,
    isLoading: isCalculating,
    error,
  } = useSWR(
    !isLoadingData && filteredEntries && filteredEntries.length > 0
      ? ['metric-calculation', config.metricType, filteredEntries.length]
      : null,
    () => calculateMetric(config, filteredEntries || []),
    {
      revalidateOnFocus: false,
      dedupingInterval: 30000, // Cache for 30 seconds
    }
  )

  return {
    value: result?.value || 0,
    isLoading: isLoadingData || isCalculating,
    error: error?.message || result?.error,
  }
}
